import type { ReactNode } from 'react';
import { ApiError, remedyFor } from '../../api/errors';
import { formatClock } from '../../lib/format';

type Tone = 'error' | 'warning' | 'info';

const TONE_CLASS: Record<Tone, string> = {
  error:
    'border-red-300 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/50 dark:text-red-100',
  warning:
    'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-100',
  info: 'border-sky-300 bg-sky-50 text-sky-950 dark:border-sky-900/60 dark:bg-sky-950/40 dark:text-sky-100',
};

function ToneIcon({ tone }: { tone: Tone }) {
  return (
    <span aria-hidden="true" className="mt-0.5 shrink-0 text-base leading-none">
      {tone === 'error' ? '⛔' : tone === 'warning' ? '⚠️' : 'ℹ️'}
    </span>
  );
}

export function Notice({
  tone,
  title,
  children,
  action,
}: {
  tone: Tone;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex gap-3 rounded-md border px-3 py-2.5 text-sm ${TONE_CLASS[tone]}`}
    >
      <ToneIcon tone={tone} />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{title}</p>
        {children ? <div className="mt-0.5 opacity-90">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  );
}

/**
 * 🔴 The load-FAILED state. Distinct from `EmptyState` by construction: this component cannot render
 * without an ApiError, and `EmptyState` cannot render one. ⭐ A reader must be able to tell "nothing
 * matched" from "we could not ask" — collapsing both into "No results" is the bug this prevents.
 */
export function ErrorNotice({ error, onRetry }: { error: ApiError; onRetry?: () => void }) {
  const remedy = remedyFor(error.kind);
  const tone: Tone = error.kind === 'upstream_unavailable' ? 'warning' : 'error';
  return (
    <Notice
      tone={tone}
      title={error.message}
      action={
        onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded border border-current/30 px-2.5 py-1 text-xs font-semibold hover:bg-black/5 dark:hover:bg-white/10"
          >
            Retry
          </button>
        ) : null
      }
    >
      {error.reason ? <p className="break-words">{error.reason}</p> : null}
      {remedy ? <p>{remedy}</p> : null}
      {error.status ? (
        <p className="mt-1 font-mono text-xs opacity-70">
          HTTP {error.status} · {error.url.replace(/^https?:\/\/[^/]+/, '')}
        </p>
      ) : null}
    </Notice>
  );
}

/**
 * 🔴 Shown whenever data on screen is data we have FAILED to refresh.
 * The age is spelled out because "stale" without a time is just a word — the reader's real question
 * is "how old is what I am looking at?".
 */
export function StaleBanner({
  error,
  loadedAt,
  onRetry,
}: {
  error: ApiError;
  loadedAt: Date | null;
  onRetry?: () => void;
}) {
  return (
    <Notice
      tone="warning"
      title={`Showing data from ${loadedAt ? formatClock(loadedAt) : 'an earlier load'} — refresh failed`}
      action={
        onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded border border-current/30 px-2.5 py-1 text-xs font-semibold hover:bg-black/5 dark:hover:bg-white/10"
          >
            Retry
          </button>
        ) : null
      }
    >
      <p>
        {error.message}
        {error.reason ? ` — ${error.reason}` : ''}
      </p>
      <p>What is below is NOT current. Anything changed since then is not shown.</p>
    </Notice>
  );
}
